import { createAsyncThunk } from "@reduxjs/toolkit";
import { subcompaniesApi } from "../../api/subcompaniesApi";
import { AppDispatch, RootState } from "../store";
import { setEditMode } from "../reducers/tableManagementReducer";
import { CompanyType } from "../../types/companyManagementTypes";
import { EmployeeUserType } from "../../types/employeeType";
import { handleRequest } from "../utils/handleRequest";
import i18n from "../../i18n";

export const fetchSubCompaniesThunk = createAsyncThunk<
  CompanyType[],
  void,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "subCompanies/fetchSubCompanies",
  async (_, { dispatch, rejectWithValue }) => {
    const result = await handleRequest(
      dispatch,
      "indirect",
      () => subcompaniesApi.getSubCompanies()
    );
    if (!result.success()) {
      return rejectWithValue(result.errorMessage ?? "Failed to fetch sub-companies");
    }
    return result.result as CompanyType[];
  }
);

export const createSubCompanyThunk = createAsyncThunk<
  CompanyType,
  Omit<CompanyType, "id">,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "subCompanies/createSubCompany",
  async (subCompanyData, { dispatch, rejectWithValue }) => {
    const result = await handleRequest(
      dispatch,
      "direct",
      () => subcompaniesApi.createSubCompany(subCompanyData),
      i18n.t("requestMessage.subcompanyCreated")
    );
    if (!result.success()) {
      return rejectWithValue(result.errorMessage ?? "Failed to create sub-company");
    }
    await dispatch(fetchSubCompaniesThunk());
    dispatch(setEditMode(false));
    return result.result as CompanyType;
  }
);

export const editSubCompaniesThunk = createAsyncThunk<
  CompanyType,
  { id: number; subCompanyData: Omit<CompanyType, "id"> },
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "subCompanies/editSubCompanies",
  async ({ id, subCompanyData }, { dispatch, rejectWithValue }) => {
    const result = await handleRequest(
      dispatch,
      "direct",
      () => subcompaniesApi.editSubCompany(id, subCompanyData),
      i18n.t("requestMessage.subcompanyUpdated")
    );
    if (!result.success()) {
      return rejectWithValue(result.errorMessage ?? "Failed to update sub-company");
    }
    await dispatch(fetchSubCompaniesThunk());
    dispatch(setEditMode(false));
    return result.result as CompanyType;
  }
);

export const deleteSubCompanyThunk = createAsyncThunk<
  number,
  number,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "subCompanies/deleteSubCompany",
  async (id, { dispatch, rejectWithValue }) => {
    const result = await handleRequest(
      dispatch,
      "direct",
      () => subcompaniesApi.deleteSubCompany(id),
      i18n.t("requestMessage.subcompanyDeleted")
    );
    if (!result.success()) {
      return rejectWithValue(result.errorMessage ?? "Failed to delete sub-company");
    }
    await dispatch(fetchSubCompaniesThunk());
    dispatch(setEditMode(false));
    return id;
  }
);

export const fetchSubCompanyEmployeesThunk = createAsyncThunk<
  EmployeeUserType[],
  number,
  { dispatch: AppDispatch; state: RootState; rejectValue: string }
>(
  "subCompanies/fetchSubCompanyEmployees",
  async (subCompanyId, { dispatch, rejectWithValue }) => {
    const result = await handleRequest(
      dispatch,
      "indirect",
      () => subcompaniesApi.getSubCompanyEmployees(subCompanyId)
    );

    if (!result.success()) {
      return rejectWithValue(result.errorMessage ?? "Failed to fetch employees");
    }

    return result.result as EmployeeUserType[];
  }
);